import { Button, Group, Stack, TextInput, useMantineTheme } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { findIndex, hasAtLeast, isDefined, isEmpty, keys, last } from 'remeda';

import type { ClientForm } from '../hooks/useClientForm';

import { FieldRemoveControl } from './FieldRemoveControl';

export type IbanBicPair = {
  id?: string;
  iban: string;
  bic: string;
  _destroy?: boolean;
};

type MultiIbanBicInputProps = {
  form: ClientForm;
};

export const MultiIbanBicInput = ({ form }: MultiIbanBicInputProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { bankAccounts } = form.values;

  const shouldShowError = keys(form.errors).some(
    key => key.startsWith('bankAccounts.') && (key.includes('.iban') || key.includes('.bic')),
  );

  const addBankAccountInput = () => {
    form.insertListItem('bankAccounts', {
      iban: '',
      bic: '',
    });
  };

  const deleteBankAccount = (index: number) => {
    const account = bankAccounts[index];

    if (isDefined(account.id)) {
      const updatedBankAccounts = bankAccounts.map((a, i) =>
        i === index ? { ...a, _destroy: true } : a,
      );
      form.setFieldValue('bankAccounts', updatedBankAccounts);
    } else {
      form.removeListItem('bankAccounts', index);
    }
  };

  const handleBankAccountChange = (
    originalIndex: number,
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'iban' | 'bic',
    pair: IbanBicPair,
  ) => {
    if (isEmpty(event.currentTarget.value.trim()) && isEmpty(pair.bic.trim())) {
      deleteBankAccount(originalIndex);
      addBankAccountInput();
    }
    form.setFieldValue(`bankAccounts.${originalIndex}.${type}`, event.currentTarget.value);
  };

  const visibleBankAccounts = bankAccounts.filter(account => !account._destroy);

  const canAddIbanBic = () => {
    if (!hasAtLeast(visibleBankAccounts, 1)) return false;

    const lastIban = last(visibleBankAccounts).iban.trim();
    return lastIban.length < 14;
  };

  const ibanBicInputFields = visibleBankAccounts.map((pair, visibleIndex) => {
    const originalIndex = findIndex(bankAccounts, a => (pair.id ? a.id === pair.id : a === pair));

    return (
      <Group key={pair.id || `bank-account-${originalIndex}`} align="center" gap="sm">
        <Group gap="sm" align="start" style={{ flex: 1 }}>
          <TextInput
            {...form.getInputProps(`bankAccounts.${originalIndex}.iban`)}
            key={form.key(`bankAccounts.${originalIndex}.iban`)}
            onChange={event => handleBankAccountChange(originalIndex, event, 'iban', pair)}
            flex={2}
            label={`IBAN ${visibleIndex + 1}`}
            placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.IBAN)}
            value={pair.iban}
            maxLength={34}
          />
          <TextInput
            {...form.getInputProps(`bankAccounts.${originalIndex}.bic`)}
            key={form.key(`bankAccounts.${originalIndex}.bic`)}
            onChange={event => handleBankAccountChange(originalIndex, event, 'bic', pair)}
            flex={1}
            label={`BIC ${visibleIndex + 1}`}
            placeholder={t(i18nKeys.FORM.CLIENT.PLACEHOLDER.BIC)}
            value={pair.bic}
            maxLength={11}
          />
        </Group>
        <FieldRemoveControl
          data={visibleBankAccounts}
          mt="lg"
          disabled={isEmpty(pair.iban.trim())}
          onClear={() => {
            deleteBankAccount(originalIndex);
            addBankAccountInput();
          }}
          onRemove={() => deleteBankAccount(originalIndex)}
        />
      </Group>
    );
  });

  return (
    <Panel
      title={t(i18nKeys.BANK_ACCOUNTS)}
      style={{ borderColor: shouldShowError ? theme.colors.error[5] : theme.colors.gray[2] }}
    >
      <Stack gap="sm">
        {ibanBicInputFields}
        <Group justify="end" mt="lg">
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={addBankAccountInput}
            size="sm"
            disabled={canAddIbanBic()}
          >
            {t(i18nKeys.FORM.CLIENT.ADD_BANK_ACCOUNT)}
          </Button>
        </Group>
      </Stack>
    </Panel>
  );
};
